"use client";
// @ts-ignore
import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Minus, Plus, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface ServicePaymentPanelProps {
  serviceName: string;
  specialist?: string;
  duration?: string;
  quantity?: number;
  price?: number;      // price per unit (raw)
  discount?: number;   // percent (0..100)
  total?: number;      // total after discount (per service line)
  paid?: number;       // paid amount for this service (if tracked)
  disabled?: boolean;  // disable editing when paid
  onQuantityChange?: (quantity: number) => void;
  /** Callback when discount percentage is changed */
  onDiscountChange?: (discountPercent: number) => void;
  /** Callback when the total price is manually changed */
  onPriceChange?: (newTotalPrice: number) => void;
  /** Callback when service should be removed */
  onRemove?: () => void;
}

export function ServicePaymentPanel({
  serviceName,
  specialist,
  duration,
  quantity = 1,
  price = 0,
  discount = 0,
  total,
  paid = 0,
  disabled = false,
  onQuantityChange,
  onDiscountChange,
  onPriceChange,
  onRemove,
}: ServicePaymentPanelProps) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  // Local editing state for discount and price inputs
  const [editingDiscount, setEditingDiscount] = useState<string | undefined>(undefined);
  const [editingTotal, setEditingTotal] = useState<string | undefined>(undefined);

  const qty = Number.isFinite(quantity) ? quantity : 1;
  const unitPrice = Number.isFinite(price) ? price : 0;
  const subtotal = unitPrice * qty;                     // before discount
  const discountValue = discount > 0 ? (subtotal * discount) / 100 : 0;
  const computedTotal = typeof total === "number" ? total : Math.max(0, subtotal - discountValue);

  const handleDecrement = () => {
    if (qty > 1 && onQuantityChange) {
      onQuantityChange(qty - 1);
    }
  };

  const handleIncrement = () => {
    if (onQuantityChange) {
      onQuantityChange(qty + 1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    const numValue = parseInt(value, 10);
    if (onQuantityChange && Number.isFinite(numValue) && numValue >= 1) {
      onQuantityChange(numValue);
    }
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!Number.isFinite(value) || value < 1) {
      // Reset to 1 if invalid
      if (onQuantityChange) onQuantityChange(1);
    }
  };

  // Discount input handlers
  const handleDiscountInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingDiscount(e.target.value);
  };

  const handleDiscountInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!onDiscountChange) return;
    const raw = String(e.target.value || "").replace(",", ".");
    const val = Math.max(0, Math.min(100, Number(raw) || 0));
    onDiscountChange(val);
    setEditingDiscount(undefined);
  };

  const handleDiscountInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setEditingDiscount(String(discount ?? 0));
    e.target.select();
  };

  // Price input handlers
  const handlePriceInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingTotal(e.target.value);
  };

  const handlePriceInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!onPriceChange) return;
    const raw = String(e.target.value || "").replace(",", ".");
    const val = Math.max(0, Number(raw) || 0);
    onPriceChange(val);
    setEditingTotal(undefined);
  };

  const handlePriceInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setEditingTotal(computedTotal.toFixed(2));
    e.target.select();
  };

  const handlePriceDecrement = () => {
    if (!onPriceChange) return;
    const next = Math.max(0, computedTotal - 0.50);
    onPriceChange(next);
    setEditingTotal(next.toFixed(2));
  };

  const handlePriceIncrement = () => {
    if (!onPriceChange) return;
    const next = computedTotal + 0.50;
    onPriceChange(next);
    setEditingTotal(next.toFixed(2));
  };

  // Whether price/discount editing is available
  const canEditDiscount = !!onDiscountChange && !disabled;
  const canEditPrice = !!onPriceChange && !disabled;

  return (
    <div
      className={cn(
        "rounded-lg border bg-background",
        "px-3 py-3"
      )}
    >
      {/* Summary row */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "w-full flex items-center gap-3 text-left",
          "focus:outline-none"
        )}
        aria-expanded={open}
      >
        <div className="min-w-0 flex-1">
          <div className="text-[12px] font-medium leading-tight truncate">
            {serviceName}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[12px] font-semibold">
            {formatCurrency(computedTotal)}
          </div>
        </div>
        <ChevronDown
          className={cn(
            "ml-2 h-4 w-4 shrink-0 transition-transform",
            open && "rotate-180"
          )}
        />
        {onRemove && !disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="ml-2 p-1 text-gray-400 hover:text-red-500"
            aria-label={t("appointment.payment.delete_service", "Delete service")}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </button>

      {/* Details */}
      {open && (
        <div className="mt-3 space-y-3">
          {/* Quantity editor - similar to desktop payment section */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {t("appointment.payment.quantity", "Qty")}
            </span>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                size="icon"
                variant="outline"
                className="h-8 w-8"
                onClick={handleDecrement}
                disabled={disabled || qty <= 1 || !onQuantityChange}
                aria-label={t("common.decrease", "Decrease")}
              >
                <Minus className="h-3.5 w-3.5" />
              </Button>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="w-12 h-8 text-center [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                value={String(qty)}
                disabled={disabled || !onQuantityChange}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onWheel={(e) => (e.currentTarget as HTMLElement).blur()}
                aria-label={t("appointment.payment.quantity", "Quantity")}
              />
              <Button
                type="button"
                size="icon"
                variant="outline"
                className="h-8 w-8"
                onClick={handleIncrement}
                disabled={disabled || !onQuantityChange}
                aria-label={t("common.increase", "Increase")}
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Discount editor */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {t("appointment.payment.discount_percent", "Discount, %")}
            </span>
            {canEditDiscount ? (
              <Input
                type="text"
                inputMode="decimal"
                className="w-16 h-8 text-center [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                value={editingDiscount ?? (discount ? String(discount) : "0")}
                disabled={disabled}
                onChange={handleDiscountInputChange}
                onBlur={handleDiscountInputBlur}
                onFocus={handleDiscountInputFocus}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                aria-label={t("appointment.payment.discount_in_percent", "Discount in %")}
              />
            ) : (
              <span className="text-sm">{discount > 0 ? `${discount}%` : "—"}</span>
            )}
          </div>

          {/* Price/Amount editor with stepper */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {t("appointment.payment.amount_byn", "Amount")}
            </span>
            {canEditPrice ? (
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className="h-8 w-8"
                  onClick={handlePriceDecrement}
                  disabled={disabled}
                  aria-label={t("appointment.payment.decrease_amount", "Decrease amount")}
                >
                  <Minus className="h-3.5 w-3.5" />
                </Button>
                <Input
                  type="text"
                  inputMode="decimal"
                  className="w-20 h-8 text-center [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                  value={editingTotal ?? computedTotal.toFixed(2)}
                  disabled={disabled}
                  onChange={handlePriceInputChange}
                  onBlur={handlePriceInputBlur}
                  onFocus={handlePriceInputFocus}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  onWheel={(e) => (e.currentTarget as HTMLElement).blur()}
                  aria-label={t("appointment.payment.amount_in_byn", "Amount")}
                />
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className="h-8 w-8"
                  onClick={handlePriceIncrement}
                  disabled={disabled}
                  aria-label={t("appointment.payment.increase_amount", "Increase amount")}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <span className="text-sm font-medium">{formatCurrency(computedTotal)}</span>
            )}
          </div>

          {/* Other details in grid */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div className="text-muted-foreground">{t("appointment.payment.specialist", "Specialist")}</div>
            <div>{specialist || "—"}</div>

            <div className="text-muted-foreground">{t("appointment.payment.duration", "Duration")}</div>
            <div>{duration || "—"}</div>

            <div className="text-muted-foreground">{t("appointment.payment.unit_price", "Unit price")}</div>
            <div>{formatCurrency(unitPrice)}</div>

            <div className="text-muted-foreground">{t("appointment.payment.subtotal", "Subtotal")}</div>
            <div>{formatCurrency(subtotal)}</div>

            <div className="text-muted-foreground">{t("appointment.payment.paid", "Paid")}</div>
            <div>{formatCurrency(paid)}</div>
          </div>
        </div>
      )}
    </div>
  );
}
